import {
    type Application,
    ApplicationType,
    type CreateApplicationRequest,
    type CreateVersionRequest
} from '../types/ai-apps';
import { baseUrl, loadApplications } from '../api/applications';
import {
    API_BRIDGE_API_START_MARKER,
    API_BRIDGE_CONTENT_PARAMETER_NAME,
    API_BRIDGE_FILENAME,
    API_BRIDGE_URL_PARAMETER_NAME,
    APP_BRIDGE_LOCAL_URL,
    MODULE_BASE_URL,
    START_POSITION_IN_APPS_MENU
} from '../constants/ai-apps';
import { usePermission } from '../composables/usePermission';
import { useMenuStore } from 'vince-core/stores/menuStore';
import { Menu } from 'vince-core/types/menu';
import { getApplicationBaseUrl } from 'vince-core/utils/env';
import { useUserStore } from 'vince-core/stores/userStore';
import { ResourceType } from 'vince-core/types/common';
import { Action } from 'vince-core/types/roles';

export const userHasAccessToApps = (): boolean => {
    const userStore = useUserStore();
    const permissions = usePermission();
    return userStore.userPermission?.permissions.some(
        perm => perm.vrn.includes(VinceApp.AI_APPS + ':' + ResourceType.APP) && perm.action === Action.READ
    ) || permissions.hasPermission({ resourceId: '*', action: Action.READ });
};

export const createNewApplicationRequest = (): CreateApplicationRequest => {
    return {
        name: '',
        description: '',
        labels: [],
        resources: [],
        connectionId: undefined
    };
};

export const createNewVersionRequest = (): CreateVersionRequest => {
    return {
        description: '',
        resources: [],
        connectionId: undefined,
        providedSourceUrl: '',
        source: ApplicationType.GENERATED
    };
};

export const getApplicationRunHref = (id: string): string => {
    return `${MODULE_BASE_URL}/${id}/run`;
};

export const refreshApplicationsMenu = async () => {
    const store = useMenuStore();
    let startPosition = START_POSITION_IN_APPS_MENU;
    const favouriteApps = await loadApplications({ onlyFavourites: true });
    for (const app of favouriteApps) {
        store.addAppMenuItem(app.name, getApplicationRunHref(app.id), startPosition, undefined); // TODO rights to run app
        startPosition++;
    }
};

export const removeApplicationFromMenu = (id: string) => {
    const store = useMenuStore();
    store.removeMenuItem(getApplicationRunHref(id), Menu.APPS);
};

export const getStartUrl = (applicationId: string, sessionId: string, indexPage: string):string => {
    return `${baseUrl}/${applicationId}/sessions/${sessionId}/assets/${indexPage}`;
};

export const addApplicationToMenu = (application: Application) => {
    const store = useMenuStore();
    const positions = store.appsMenu.map(it => it.position);
    const startPosition = positions.length > 0 ? Math.max(...positions) + 1 : START_POSITION_IN_APPS_MENU;
    store.addAppMenuItem(application.name, getApplicationRunHref(application.id), startPosition, undefined); // TODO rights to run app
};

export const defaultBuildParameters = async (): Promise<Record<string, string>> => {
    let apiBridgeUrl = window.location.protocol + '//' +
        [window.location.host, getApplicationBaseUrl(), API_BRIDGE_FILENAME].join('/').replace(/\/+/g, '/');

    if (window.location.hostname.includes('localhost')) {
        apiBridgeUrl = APP_BRIDGE_LOCAL_URL;
    }

    const apiBridgeContent = (await (await fetch(apiBridgeUrl)).text()).split(API_BRIDGE_API_START_MARKER)[1]!;

    // Tells the bridge script which origin embeds the app, so it can verify
    // inbound host events and pin the target of outbound publishes.
    apiBridgeUrl += `?origin=${encodeURIComponent(window.location.origin)}`;

    return {
        [API_BRIDGE_URL_PARAMETER_NAME]: apiBridgeUrl,
        [API_BRIDGE_CONTENT_PARAMETER_NAME]: apiBridgeContent
    };
};
